import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Grid, Paper, IconButton, Avatar, Stack, Divider, Button } from '@mui/material';
import { Search as SearchIcon, ArrowBackIosNew, TrendingUp, PersonOutline, ArticleOutlined } from '@mui/icons-material';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { auth, db } from './firebase';
import { useTranslatedDictionary } from './hooks/useTranslatedDictionary';
import PostMedia from './PostMedia';
import PostTextBlock from './PostTextBlock';
import { isPublicPost } from './postAudience';

const BUSCAR_TEXT = {
  es: {
    placeholder: 'Buscar en Gexvi...',
    all: 'Todo',
    trends: 'Tendencias',
    noTrends: 'No hay tendencias todavía.',
    publishHashtags: 'Publica con hashtags para empezar',
    people: 'Personas',
    posts: 'Publicaciones',
    user: 'Usuario',
    usernameFallback: 'usuario',
    noResults: 'No encontramos resultados para',
    noUsers: 'No hay usuarios para mostrar.',
    noPosts: 'No hay publicaciones para mostrar.',
  },
  en: {
    placeholder: 'Search on Gexvi...',
    all: 'All',
    trends: 'Trends',
    noTrends: 'There are no trends yet.',
    publishHashtags: 'Post with hashtags to get started',
    people: 'People',
    posts: 'Posts',
    user: 'User',
    usernameFallback: 'user',
    noResults: 'No results found for',
    noUsers: 'No users to show.',
    noPosts: 'No posts to show.',
  },
};

const Buscar = ({ onBack, onOpenProfile, language = 'es', themeMode = 'oscuro' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [posts, setPosts] = useState([]);
  const [usersFromCollection, setUsersFromCollection] = useState([]);
  const [hiddenUids, setHiddenUids] = useState([]);
  const t = useTranslatedDictionary(BUSCAR_TEXT, language);
  const isLightTheme = themeMode === 'claro';
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;

  useEffect(() => {
    const postsQuery = query(collection(db, 'posts'), orderBy('timestamp', 'desc'), limit(80));
    const unsubscribe = onSnapshot(postsQuery, (snapshot) => {
      // El buscador muestra solo contenido público.
      setPosts(snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })).filter(isPublicPost));
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const myUid = auth.currentUser?.uid;
    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      setHiddenUids(
        snapshot.docs
          .filter((docItem) => docItem.id !== myUid && docItem.data()?.apareceEnBusqueda === false)
          .map((docItem) => docItem.id)
      );
      setUsersFromCollection(
        snapshot.docs
          .filter((docItem) => docItem.id === myUid || docItem.data()?.apareceEnBusqueda !== false)
          .map((docItem) => {
            const data = docItem.data() || {};
            return {
              uid: docItem.id,
              handle: data.handle || data.username || '',
              username: data.displayName || data.username || data.handle || '',
              userImg: data.photoURL || data.photoUrl || data.userImg || data.avatar || '',
            };
          })
      );
    });

    return () => unsubscribe();
  }, []);

  const trends = useMemo(() => {
    const map = new Map();

    posts.forEach((post) => {
      const text = post?.text || '';
      const tags = text.match(/#[\p{L}\p{N}_]+/gu) || [];
      tags.forEach((tag) => {
        const normalized = tag.toLowerCase();
        map.set(normalized, (map.get(normalized) || 0) + 1);
      });
    });

    return [...map.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([tag]) => tag);
  }, [posts]);

  const results = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();
    if (!value) return [];

    return posts.filter((post) => {
      const text = (post?.text || '').toLowerCase();
      const username = (post?.username || '').toLowerCase();
      const handle = (post?.handle || '').toLowerCase();
      return text.includes(value) || username.includes(value) || handle.includes(value);
    });
  }, [posts, searchTerm]);

  const users = useMemo(() => {
    const map = new Map();
    usersFromCollection.forEach((user) => {
      const key = (user.uid || user.handle || '').trim();
      if (!key || map.has(key)) return;
      map.set(key, user);
    });

    posts.forEach((post) => {
      const key = (post.uid || post.handle || '').trim();
      if (key && !map.has(key) && !hiddenUids.includes(post.uid)) {
        map.set(key, {
          uid: post.uid || null,
          handle: post.handle,
          username: post.username,
          userImg: post.userImg,
        });
      }
    });
    return [...map.values()];
  }, [posts, usersFromCollection, hiddenUids]);

  const handleOpenProfile = (uid) => {
    if (!uid || typeof onOpenProfile !== 'function') return;
    onOpenProfile(uid);
  };

  const filteredUsers = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();
    if (!value) return [];
    return users.filter(
      (u) =>
        (u.username || '').toLowerCase().includes(value) ||
        (u.handle || '').toLowerCase().includes(value)
    );
  }, [users, searchTerm]);

  const hasSearch = Boolean(searchTerm.trim());

  const displayedUsers = useMemo(() => {
    if (hasSearch) return filteredUsers;
    return users.slice(0, 20);
  }, [hasSearch, filteredUsers, users]);

  const displayedPosts = useMemo(() => {
    if (hasSearch) return results;
    return posts.slice(0, 20);
  }, [hasSearch, results, posts]);

  const showTrends = activeCategory === 'all' || (!hasSearch && activeCategory === 'trends');
  const showPeople = hasSearch
    ? activeCategory === 'all' || activeCategory === 'people'
    : activeCategory === 'people';
  const showPosts = hasSearch
    ? activeCategory === 'all' || activeCategory === 'posts'
    : activeCategory === 'posts';

  const categoryButtons = [
    { key: 'all', label: t('all') },
    { key: 'trends', label: t('trends') },
    { key: 'people', label: t('people') },
    { key: 'posts', label: t('posts') },
  ];

  const cardSx = {
    p: 2,
    bgcolor: onSurface(isLightTheme ? 0.04 : 0.05),
    borderRadius: '16px',
    border: `1px solid ${onSurface(0.08)}`,
    backdropFilter: 'blur(8px)',
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        color: onSurface(1),
        background: isLightTheme ? 'linear-gradient(to bottom, #ffffff, #f2f5f8)' : 'linear-gradient(to bottom, #101010, #000)',
        p: 2,
        pt: 1,
        pb: 10,
        maxWidth: '860px',
        mx: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <IconButton onClick={onBack} sx={{ color: onSurface(1) }}>
          <ArrowBackIosNew fontSize="small" />
        </IconButton>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: onSurface(0.09),
            borderRadius: '14px',
            px: 1.5,
            height: 44,
            '&:focus-within': { bgcolor: onSurface(0.13) },
          }}
        >
          <SearchIcon sx={{ color: onSurface(0.5), fontSize: 20, flexShrink: 0 }} />
          <Box
            component="input"
            autoFocus
            placeholder={t('placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: onSurface(1),
              fontSize: '0.95rem',
              '&::placeholder': { color: onSurface(0.5) },
            }}
          />
        </Box>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}>
        {categoryButtons.map((category) => {
          const selected = activeCategory === category.key;
          return (
            <Button
              key={category.key}
              size="small"
              onClick={() => setActiveCategory(category.key)}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                px: 1.6,
                color: selected ? '#001219' : onSurface(0.78),
                bgcolor: selected ? '#00d4ff' : onSurface(0.08),
                border: selected ? '1px solid #00d4ff' : `1px solid ${onSurface(0.15)}`,
                '&:hover': {
                  bgcolor: selected ? '#00c4ec' : onSurface(0.14),
                },
              }}
            >
              {category.label}
            </Button>
          );
        })}
      </Stack>

      {/* Sin búsqueda: Tendencias */}
      {showTrends && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <TrendingUp sx={{ color: '#00d4ff' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('trends')}</Typography>
          </Stack>

          {trends.length === 0 ? (
            <Paper sx={{ ...cardSx, textAlign: 'center', py: 4 }}>
              <TrendingUp sx={{ fontSize: 40, color: onSurface(0.2), mb: 1 }} />
              <Typography variant="body2" sx={{ color: onSurface(0.5) }}>
                {t('noTrends')}
              </Typography>
              <Typography variant="caption" sx={{ color: onSurface(0.35) }}>
                {t('publishHashtags')}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={1.5}>
              {trends.map((trend) => (
                <Grid size={{ xs: 6, sm: 4 }} key={trend}>
                  <Paper
                    onClick={() => setSearchTerm(trend)}
                    sx={{
                      ...cardSx,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(0,212,255,0.1)',
                        borderColor: '#00d4ff',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#00d4ff' }}>
                      {trend}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Usuarios + Posts */}
      {(hasSearch || showPeople || showPosts) && (
        <Box>
          {/* Usuarios */}
          {showPeople && displayedUsers.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <PersonOutline sx={{ color: '#00d4ff', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {t('people')}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {displayedUsers.map((u) => (
                  <Paper
                    key={u.uid || u.handle || u.username}
                    sx={{
                      ...cardSx,
                      p: 1.5,
                      cursor: u.uid ? 'pointer' : 'default',
                    }}
                    onClick={() => handleOpenProfile(u.uid)}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={u.userImg} sx={{ width: 44, height: 44 }} />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: onSurface(1) }}>
                          {u.username || t('user')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: onSurface(0.5) }}>
                          @{u.handle || t('usernameFallback')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          {showPeople && displayedUsers.length === 0 && !hasSearch && (
            <Paper sx={{ ...cardSx, textAlign: 'center', py: 4, mb: 3 }}>
              <Typography variant="body2" sx={{ color: onSurface(0.5) }}>
                {t('noUsers')}
              </Typography>
            </Paper>
          )}

          {showPeople && showPosts && displayedUsers.length > 0 && displayedPosts.length > 0 && (
            <Divider sx={{ bgcolor: onSurface(0.08), mb: 2 }} />
          )}

          {/* Posts */}
          {showPosts && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <ArticleOutlined sx={{ color: '#00d4ff', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('posts')} ({displayedPosts.length})
              </Typography>
            </Stack>
          )}

          {hasSearch &&
          (showPeople ? displayedUsers.length === 0 : true) &&
          (showPosts ? displayedPosts.length === 0 : true) ? (
            <Paper sx={{ ...cardSx, textAlign: 'center', py: 5 }}>
              <SearchIcon sx={{ fontSize: 44, color: onSurface(0.2), mb: 1 }} />
              <Typography variant="body2" sx={{ color: onSurface(0.5) }}>
                {t('noResults')} "{searchTerm}"
              </Typography>
            </Paper>
          ) : showPosts && displayedPosts.length > 0 ? (
            <Stack spacing={1.2}>
              {displayedPosts.map((post) => (
                <Paper
                  key={post.id}
                  sx={{
                    ...cardSx,
                    p: 1.8,
                    cursor: post.uid ? 'pointer' : 'default',
                  }}
                  onClick={() => handleOpenProfile(post.uid)}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: post.text ? 1 : 0 }}>
                    <Avatar src={post.userImg} sx={{ width: 38, height: 38 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: onSurface(1) }}>
                        {post.username || t('user')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: onSurface(0.5) }}>
                        @{post.handle || t('usernameFallback')}
                      </Typography>
                    </Box>
                  </Stack>
                  <PostTextBlock text={post.text} textBackground={post.textBackground} compact isLightTheme={isLightTheme} />
                  <PostMedia post={post} maxHeight={220} isLightTheme={isLightTheme} language={language} />
                </Paper>
              ))}
            </Stack>
          ) : (
            showPosts && !hasSearch && (
              <Paper sx={{ ...cardSx, textAlign: 'center', py: 4 }}>
                <Typography variant="body2" sx={{ color: onSurface(0.5) }}>
                  {t('noPosts')}
                </Typography>
              </Paper>
            )
          )}
        </Box>
      )}
    </Box>
  );
};

export default Buscar;